import React, { useState, useEffect } from "react";
import axios from "axios";
import { getAuthHeaders } from '../utils/getAuthHeaders'
import { getErrorMessage } from '../utils/getErrorMessage'
import { endpoints } from "../config/api";

const UploadOrgIcon = ({ onClose }) => {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // Fetch the list of orgs from the API
    const fetchOrgs = async () => {
      try {
        const response = await axios.get(
          endpoints.orgs,
          {
            headers: {
              ...getAuthHeaders(),
              Accept: "*/*",
              "Content-Type": "application/json",
            },
          }
        );
        setOrgs(response.data);
      } catch (error) {
        console.error("Error fetching orgs", error);
        setErrorMessage("Failed to fetch organizations.");
      }
    };

    fetchOrgs();
  }, []);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleOrgChange = (event) => {
    setSelectedOrgId(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile || !selectedOrgId) {
      setErrorMessage("Please select an organization and a file.");
      return;
    }

    const authHeaders = getAuthHeaders();
    if (!authHeaders.Authorization) {
      setErrorMessage("Please log in to upload an icon.");
      return;
    }

    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("organizationId", selectedOrgId);

    try {
      const response = await axios.post(
        endpoints.storageUploadIcon,
        formData,
        {
          headers: {
            ...authHeaders,
            // Let axios set the correct multipart boundary for FormData.
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        // Adjust status codes as per your API
        setSuccessMessage("Icon uploaded successfully!");
        setSelectedFile(null);
        // Optionally reset selectedOrgId or clear the file input
        // document.getElementById('icon-upload-file-input').value = null;
      } else {
        setErrorMessage(typeof response.data.message === "string" ? response.data.message : "Failed to upload icon.");
      }
    } catch (error) {
      console.error("Error uploading icon", error?.response);
      setErrorMessage(getErrorMessage(error, "Failed to upload icon. Please contact the team to fix."));
    } finally {
      setLoading(false);
    }
  };

  const headingStyle = {
    color: "#3730a3",
    fontSize: "28px",
    fontWeight: "bold",
    marginBottom: "24px",
    lineHeight: "1.2"
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    fontSize: "14px",
    backgroundColor: "#ffffff",
    border: "1px solid #3730a3",
    borderRadius: "6px",
    boxSizing: "border-box",
    color: "#1f2937",
    outline: "none"
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer"
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3730a3"
  };

  const buttonStyle = {
    padding: "12px 24px",
    fontSize: "14px",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "background-color 0.2s"
  };

  const formGroupStyle = {
    marginBottom: "20px"
  };

  const successMessageStyle = {
    color: "#10b981",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#d1fae5",
    borderRadius: "6px",
    border: "1px solid #10b981"
  };

  const errorMessageStyle = {
    color: "#ef4444",
    marginBottom: "16px",
    padding: "12px",
    backgroundColor: "#fee2e2",
    borderRadius: "6px",
    border: "1px solid #ef4444"
  };

  return (
    <div>
      {onClose && (
        <button
          style={{
            height: "40px",
            padding: "0 16px",
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#3730a3",
            color: "#ffffff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "600",
            marginBottom: "20px",
          }}
          onClick={onClose}
        >
          Close
        </button>
      )}
      <h2 style={headingStyle}>Upload Organization Icon</h2>
      {successMessage && (
        <div style={successMessageStyle}>{successMessage}</div>
      )}
      {errorMessage && (
        <div style={errorMessageStyle}>{errorMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={formGroupStyle}>
          <label
            htmlFor="org-select"
            style={labelStyle}
          >
            Select Organization
          </label>
          <select
            id="org-select"
            value={selectedOrgId}
            onChange={handleOrgChange}
            required
            style={selectStyle}
          >
            <option value="">Select an organization</option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        </div>

        <div style={formGroupStyle}>
          <label
            htmlFor="icon-upload-file-input"
            style={labelStyle}
          >
            Icon File
          </label>
          <input
            id="icon-upload-file-input"
            type="file"
            onChange={handleFileChange}
            accept="image/*" // Suggests to the browser to filter for image files
            required
            style={inputStyle}
          />
        </div>

        <button
          type="submit"
          disabled={loading || !selectedFile || !selectedOrgId}
          style={{
            ...buttonStyle,
            opacity: (loading || !selectedFile || !selectedOrgId) ? 0.6 : 1,
            cursor: (loading || !selectedFile || !selectedOrgId) ? "not-allowed" : "pointer"
          }}
        >
          {loading ? "Uploading..." : "Upload Icon"}
        </button>
      </form>
    </div>
  );
};

export default UploadOrgIcon;
